import api from '@core/services/api/_api';
import util from '@core/services/util';
import ENUMS from '@core/classes/Enums';
import DocumentAssociation from '@core/classes/DocumentAssociation';
import PlaidTransferIntent from '@core/classes/Plaid/PlaidTransferIntent';
import { PlaidIncomeVerificationConfig } from '@core/classes/Plaid/PlaidIncomeVerification';
import { PlaidStatementsConfig } from '@core/classes/Plaid/PlaidStatements';
import { PlaidUserInfo, PlaidUserInfoRequest } from '@core/classes/Plaid/PlaidUserInfo';
import { PlaidAccessToken, PlaidCustomerInfo } from '@core/classes/Plaid/PlaidCustomerInfo';
import PlaidLinkTokenMetadataResponse from '@core/classes/Plaid/PlaidLinkTokenMetadataResponse';

// Configure and Initialize a Plaid Link Session
// Plaid API Docs: https://plaid.com/docs/api/tokens/#linktokencreate

export default class PlaidLinkConfig {
	#transferIntentId?: string = null;

	/** REQUIRED (with at least one customer-type association) */
	associations: DocumentAssociation[];

	/**
	 * REQUIRED. Array of [ENUMS.PLAID_PRODUCTS].
	 * These products will be billed once the Item is created.
	 * Institutions that don't support these products will be filtered out from the bank selection screen.
	 * */
	productsToInit: string[];

	/**
	 * Array of [ENUMS.PLAID_PRODUCTS].
	 * Cannot overlap with any other products list.
	 * If available, will added to Item and billed on Item creation (does not filter institution list).
	 * [Plaid Docs](https://plaid.com/docs/link/initializing-products/#required-if-supported-products)
	 */
	productsRequiredIfSupported?: string[] = [];

	/**
	 * Array of [ENUMS.PLAID_PRODUCTS]
	 * Cannot overlap with any other products list
	 * Plaid makes a "best-effort" to add the product; product will not be billed until their relative endpoints are called
	 * [Plaid Docs](https://plaid.com/docs/link/initializing-products/#optional-products)
	 */
	productsOptional?: string[] = [];

	/** Name Link will use to refer to your app */
	name?: string = 'Sample App';

	/** ENUMS.PLAID_LANGUAGES; The language that Link should be displayed in; Ignored if initing with Identity Verification. Defaults to English if nothing is provided. */
	linkLanguage?: string = ENUMS.PLAID_LANGUAGES.English;

	/**
	 * Array of [ENUMS.PLAID_COUNTRIES]. If using IDV, should use the country where your company is based. Otherwise, represents the country of your user's financial institution.
	 * Defaults to United States if nothing is provided.
	 * For a list of supported countries, refer to the [Plaid Docs](https://plaid.com/global/).
	 */
	countries?: string[] = [ENUMS.PLAID_COUNTRIES.UnitedStates];

	/** Name of Link Customization; Language / country need to match customization if using */
	linkCustomization?: string;

	/** Optional, only for **IDENTITY VERIFICATION**; Defaults to APPSETTINGS template ID if none passed in */
	templateId?: string;

	/** Required only for **TRANSFER** product */
	transferIntent?: PlaidTransferIntent;

	/** Required only for **INCOME VERIFICATION** product */
	incomeVerification?: PlaidIncomeVerificationConfig;

	/** Required only for **STATEMENTS** product */
	statements?: PlaidStatementsConfig;

	/** Any info collected about the user before Link; In IDV, can be used to skip Lightning Verification */
	userData?: PlaidUserInfo;

	/** The ID of the IDV session that gets created if prepopulateIdentityVerification gets called*/
	idvSessionId?: string;

	/** When true, enables linking multiple Items in the same flow. Not available for non-banking products. */
	enableMultiItemLink?: boolean;

	/**
	 * Used only when using Plaid Sandbox. Allows you to test various Returning User Experience scenarios. When provided, will also override the link customization name
	 * to "REMEMBER_ME_SANDBOX".
	 */
	enableSandboxRUX?: SandboxRUXTestCase;

	constructor(init?: Partial<PlaidLinkConfig>) {
		this.associations = init?.associations ?? [];
		this.productsToInit = init?.productsToInit ?? [];
		this.productsRequiredIfSupported = init?.productsRequiredIfSupported ?? [];
		this.productsOptional = init?.productsOptional ?? [];
		this.name = init?.name ?? 'Sample App';
		this.linkLanguage = init?.linkLanguage ?? ENUMS.PLAID_LANGUAGES.English;
		this.countries = init?.countries ?? [ENUMS.PLAID_COUNTRIES.UnitedStates];
		this.linkCustomization = init?.linkCustomization;
		this.templateId = init?.templateId;
		this.transferIntent = init?.transferIntent;
		this.incomeVerification = init?.incomeVerification;
		this.statements = init?.statements;
		this.userData = init?.userData;
		this.idvSessionId = init?.idvSessionId;
		this.enableMultiItemLink = init?.enableMultiItemLink;
		this.enableSandboxRUX = init?.enableSandboxRUX;
	}

	populateWithLinkTokenMetadata(linkTokenResponse: PlaidLinkTokenMetadataResponse) {
		this.productsToInit = linkTokenResponse.initialProducts;
		this.countries = linkTokenResponse.countryCodes;
		this.linkLanguage = linkTokenResponse.language;
		this.name = linkTokenResponse.clientName;
	}

	// ====================
	// PLAID API FUNCTIONS
	// ====================

	/**
	 * Makes the necessary API calls to Plaid that need to be called *before* creating the Link Token.
	 * Only applicable when using IDV or Transfer products.
	 * @param plaidCustomerInfo Only required for IDV.
	 * @param storeCode Only required for IDV.
	 */
	async preInitTasks(customerId: string, storeCode?: string): Promise<void> {
		if (this.canPrepopulateIDV()) {
			if (!customerId) throw new Error('Cannot prepopulate IDV. Missing Customer ID.');
			if (!storeCode) throw new Error('Cannot prepopulate IDV. Store code is missing.');

			await this.prepopulateIdentityVerification(customerId, storeCode);
		}

		if (this.hasTransfer() && this.transferIntent) await this.getTransferIntentId();
	}

	async prepopulateIdentityVerification(customerId: string, storeCode: string): Promise<any> {
		if (this.userData.isPracticallyEmpty()) return null;

		const user = { ...this.userData, clientUserId: customerId };
		const response = await api.plaid.createIdentityVerification(this.templateId, user, storeCode, this.associations);

		if (response?.data) {
			this.templateId = response.data.template?.id; // Backend may have changed the templateID depending on certain parameters
			this.idvSessionId = response.data.id;
		}

		return response;
	}

	async getTransferIntentId(): Promise<any> {
		const response = await api.plaid.createTransferIntent(this.transferIntent);

		if (response?.data) this.#transferIntentId = response.data.transferIntent.id;

		return response;
	}

	/**
	 *
	 * @param plaidCustomerInfo If only doing IDV, the only think that is required is the `customerID`. More things may be required when using banking products.
	 * @param storeCode
	 * @param plaidItemId Optional. Pass in to initialize Link in update mode.
	 * @returns
	 */
	async createLinkToken(plaidCustomerInfo: Partial<PlaidCustomerInfo>, plaidItemId: string = null): Promise<any> {
		const accessToken = plaidItemId ? plaidCustomerInfo.accessTokens?.find(at => at.plaidItemId === plaidItemId) : null;

		const validProducts = plaidItemId && accessToken ? this.getValidUpdateProducts(accessToken) : this.productsToInit;

		const linkOptions: PlaidLinkTokenOptions = {
			userTokenId: plaidCustomerInfo?.userToken && validProducts.length > 0 ? plaidCustomerInfo.userToken.userId : null,
			plaidItemId: plaidItemId,
			clientName: this.name,
			clientUserId: plaidCustomerInfo.customerId,
			products: plaidItemId && !this.canUpdateWithProducts() ? [] : validProducts,
			requiredIfSupportedProducts: !plaidItemId ? this.productsRequiredIfSupported : null,
			optionalProducts: !plaidItemId ? this.productsOptional : null,
			language: this.linkLanguage,
			countryCodes: this.countries,
			linkCustomizationName: this.linkCustomization,
			statementsOptions: !plaidItemId ? this.statements : null,
			enableMultiItemLink: this.enableMultiItemLink,
		};

		if (this.incomeVerification && validProducts.length > 0) {
			linkOptions.incomeVerificationOptions = {
				plaidItemIds: plaidCustomerInfo.accessTokens?.map(i => i.plaidItemId) ?? null,
				...this.incomeVerification,
			};
		}

		if (!plaidItemId && this.hasIdentityVerification()) linkOptions.identityVerificationOptions = { templateId: this.templateId };

		if (!plaidItemId && this.hasTransfer() && this.#transferIntentId) linkOptions.transferOptions = { intentId: this.#transferIntentId };

		if (this.userData) linkOptions.user = this.userData;

		if (this.enableSandboxRUX && !this.hasIdentityVerification()) {
			linkOptions.user ??= new PlaidUserInfo();
			linkOptions.user.phoneNumber = this.getRUXPhoneNumber();
			linkOptions.linkCustomizationName = 'REMEMBER_ME_SANDBOX';
		}

		return await api.plaid.createLinkToken(linkOptions);
	}

	// ======================
	// BOOL CONDITION CHECKS
	// ======================

	hasPlaidProduct(plaidProduct: string) {
		return (
			this.productsToInit.some(p => p === plaidProduct) ||
			this.productsRequiredIfSupported.some(p => p === plaidProduct) ||
			this.productsOptional.some(p => p === plaidProduct)
		);
	}

	hasIncomeVerification(): boolean {
		return this.hasPlaidProduct(ENUMS.PLAID_PRODUCTS.IncomeVerification);
	}

	hasIdentityVerification(): boolean {
		return this.hasPlaidProduct(ENUMS.PLAID_PRODUCTS.IdentityVerification);
	}

	hasTransfer(): boolean {
		return this.hasPlaidProduct(ENUMS.PLAID_PRODUCTS.Transfer);
	}

	hasStatements(): boolean {
		return this.hasPlaidProduct(ENUMS.PLAID_PRODUCTS.Statements);
	}

	canPrepopulateIDV(): boolean {
		return !!this.userData && this.hasIdentityVerification();
	}

	isPayrollIncome(): boolean {
		return !!this.incomeVerification && this.incomeVerification.incomeSourceTypes.some(s => s === 'payroll');
	}

	canUpdateWithProducts(): boolean {
		return this.productsToInit.some(p => p === ENUMS.PLAID_PRODUCTS.Assets || p === ENUMS.PLAID_PRODUCTS.IncomeVerification);
	}

	// =======================
	// OTHER HELPER FUNCTIONS
	// =======================

	/**
	 * Returns a list of products that don't already exist on an access token.
	 */
	getValidUpdateProducts(accessToken?: PlaidAccessToken): string[] {
		if (!accessToken) return [];

		const validProducts: string[] = [];
		this.productsToInit.forEach(p => {
			if (accessToken.activeProducts.every(ap => ap !== p)) validProducts.push(p);
		});

		return validProducts;
	}

	/** When testing in Sandbox, returns the pre-seeded phone number for the selected text scenario */
	getRUXPhoneNumber(): string {
		switch (this.enableSandboxRUX) {
			case SandboxRUXTestCase.NewUser:
				return '+14155550010';
			case SandboxRUXTestCase.VerifiedUser:
				return '+14155550011';
			case SandboxRUXTestCase.VerifiedNewAccount:
				return '+14155550012';
			case SandboxRUXTestCase.VerifiedOAuth:
				return '+14155550013';
			case SandboxRUXTestCase.VerifiedNewDevice:
				return '+14155550014';
			case SandboxRUXTestCase.VerifiedAccountPreSelected:
				return '+14155550015';
			default:
				return this?.userData?.phoneNumber;
		}
	}

	static getRUXTypeList(): { display: string; value: string }[] {
		return Object.values(SandboxRUXTestCase).map(value => {
			return { display: util.formatSnakeCase(value), value: value };
		});
	}
}

enum SandboxRUXTestCase {
	NewUser = 'new_user',
	VerifiedUser = 'verified_user',
	VerifiedNewAccount = 'verified_newAccount',
	VerifiedOAuth = 'verified_OAuth',
	VerifiedNewDevice = 'verified_newDevice',
	VerifiedAccountPreSelected = 'verified_accountPreSelected',
}

/** Internal interface used when sending requests to `api.plaid.createLinkToken()`. Matches backend class with same name. */
interface PlaidLinkTokenOptions {
	/** The name of your application, as it should be displayed in the Link session. Max length of 30 characters. */
	clientName: string;

	/** OUR customer ID (or whatever ID we used to represent the individual going through the Link session) */
	clientUserId: string;

	/** The Item ID of a user's access token. Used when sending an access token through Update Mode. */
	plaidItemId?: string;

	/** The User ID associated with a customer's User Token. When provided, will associate any Items created in the session to the user.
	 *  Can be found in the customer's PlaidCustomerInfo record. */
	userTokenId?: string;

	/** ENUMS.PLAID_PRODUCTS. List of products you wish to use. When launching Link in update mode, should be omitted (unless adding Income or Assets to an Item) */
	products: string[];

	/** ENUMS.PLAID_PRODUCTS. List of products you wish to use only if the institution and accounts selected by the use support the product. */
	requiredIfSupportedProducts?: string[];

	/** ENUMS.PLAID_PRODUCTS. List of products you would *like* to have, but your app can function without. */
	optionalProducts?: string[];

	/** ENUMS.PLAID_LANGUAGES. The language that Link should be displayed in. */
	language: string;

	/** ENUMS.PLAID_COUNTRIES. List of countries that represents where the user's financial institutions are located. */
	countryCodes: string[];

	/** Name of the link customization to use. */
	linkCustomizationName?: string;

	/** Any information collected about the user in question. May be used to pre-fill some data in the Link session. */
	user?: PlaidUserInfoRequest;

	/** Options used to configure the income verificatio session. */
	incomeVerificationOptions?: PlaidIncomeVerificationOptions;

	/** Options used to configure the identity verification session. */
	identityVerificationOptions?: { templateId?: string; gaveConsent?: boolean };

	/** Options used to configure the transfer session. */
	transferOptions?: { intentId?: string; authorizationId?: string };

	/** Options used to configure the statements session. */
	statementsOptions?: PlaidStatementsConfig;

	/** When true, allows connecting multiple Items in a single Link session. */
	enableMultiItemLink?: boolean;
}

interface PlaidIncomeVerificationOptions extends PlaidIncomeVerificationConfig {
	assetReportId?: string;
	plaidItemIds?: string[];
}
